/**
 * ECDSA P-256 envelope signing — low-S strict (F4, ADR-12).
 *
 * Wire format: 64 bytes, raw r||s, each 32-byte big-endian. The signer normalizes
 * s = min(s, n−s); strict verify rejects s > n/2, r = 0, s = 0, r ≥ n, s ≥ n
 * on the raw bytes BEFORE calling WebCrypto (subtle.verify is malleable and
 * accepts high-S twins). Signature input: DAG-CBOR(envelope minus top-level sig).
 */
import type { webcrypto } from "node:crypto";
import { beBytes32, beInt, concatBytes } from "./bytes.js";
import { dagCborEncode, type CborValue } from "./cbor.js";
import { jwkFromDidKey, type Jwk } from "./didkey.js";

export type CryptoKey = webcrypto.CryptoKey;

export const P256_N = BigInt(
  "0xFFFFFFFF00000000FFFFFFFFFFFFFFFFBCE6FAADA7179E84F3B9CAC2FC632551",
);
const HALF_N = P256_N >> 1n;

/** DAG-CBOR encoding of the envelope with the top-level `sig` key removed. */
export function signingInput(envelope: Record<string, CborValue>): Uint8Array {
  const { sig: _sig, ...rest } = envelope;
  return dagCborEncode(rest);
}

export async function signEnvelope(
  envelope: Record<string, CborValue>,
  privateKey: CryptoKey,
): Promise<Uint8Array> {
  const raw = new Uint8Array(
    await crypto.subtle.sign(
      { name: "ECDSA", hash: "SHA-256" },
      privateKey,
      signingInput(envelope),
    ),
  );
  const r = raw.slice(0, 32);
  let s = beInt(raw.slice(32));
  if (s > HALF_N) s = P256_N - s; // low-S normalization (ADR-12)
  return concatBytes(r, beBytes32(s));
}

export async function verifyEnvelope(
  envelope: Record<string, CborValue>,
  signerDidKey: string,
): Promise<boolean> {
  const sig = envelope.sig;
  if (!(sig instanceof Uint8Array) || sig.length !== 64) return false;
  const r = beInt(sig.slice(0, 32));
  const s = beInt(sig.slice(32));
  // strict: malleability rejected on raw bytes before WebCrypto sees the sig
  if (r === 0n || s === 0n || r >= P256_N || s >= P256_N || s > HALF_N) return false;
  let pub: CryptoKey;
  try {
    pub = await importVerifyKey(signerDidKey);
  } catch {
    return false;
  }
  return crypto.subtle.verify(
    { name: "ECDSA", hash: "SHA-256" },
    pub,
    sig,
    signingInput(envelope),
  );
}

/** did:key → decompressed JWK → WebCrypto verify key. */
async function importVerifyKey(signerDidKey: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "jwk",
    jwkFromDidKey(signerDidKey),
    { name: "ECDSA", namedCurve: "P-256" },
    false,
    ["verify"],
  );
}

/** Import a private signing JWK (d/x/y). */
export async function importSigningKey(jwk: Jwk): Promise<CryptoKey> {
  // Drop key_ops/ext entirely — some WebCrypto impls reject mismatched key_ops,
  // and a present-but-undefined property is not the same as an absent one.
  const { key_ops: _ops, ext: _ext, ...cleanJwk } = jwk;
  return crypto.subtle.importKey(
    "jwk",
    cleanJwk,
    { name: "ECDSA", namedCurve: "P-256" },
    true,
    ["sign"],
  );
}

/**
 * Generate a P-256 signing keypair. Extractable because this package also powers
 * the vector generator and server-side tools; non-extractable hardening is F6's
 * client policy.
 */
export async function generateSigningKeypair(): Promise<{
  privateKey: CryptoKey;
  publicKey: CryptoKey;
  publicJwk: Jwk;
}> {
  const kp = await crypto.subtle.generateKey({ name: "ECDSA", namedCurve: "P-256" }, true, [
    "sign",
    "verify",
  ]);
  const publicJwk = (await crypto.subtle.exportKey("jwk", kp.publicKey)) as Jwk;
  return { privateKey: kp.privateKey, publicKey: kp.publicKey, publicJwk };
}
